import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import http from '../../api/http';
import { Container, Paper, Box, Typography, Alert, TextField, Button, FormControlLabel, Checkbox, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Grid from '@mui/material/Grid';

interface ErrorResponse {
  response?: {
    data?: {
      msg?: string;
      message?: string;
      errors?: string[];
      error?: string;
      details?: string[];
    };
  };
}

const genders = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: '',
    consentGiven: false,
    dataProcessingConsent: false,
    marketingConsent: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [err, setErr] = useState('');
  const [errorList, setErrorList] = useState<string[]>([]);
  const [errorDetail, setErrorDetail] = useState('');
  const [detailsList, setDetailsList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setErrorList([]);
    setErrorDetail('');
    setDetailsList([]);
    setErrors({});
    setIsLoading(true);
    
    try {
      const response = await http.post('/api/users/auth/register', formData);
      navigate('/register/success', { state: { message: response.data?.message } });
    } catch (error: any) {
      const typedError = error as ErrorResponse;
      const apiMsg = typedError?.response?.data?.message;
      const apiErrors = typedError?.response?.data?.errors;
      const apiErrorDetail = typedError?.response?.data?.error;
      const apiDetails = typedError?.response?.data?.details;
      setErr(apiMsg || 'Registration failed');
      setErrorList(Array.isArray(apiErrors) ? apiErrors : []);
      setErrorDetail(apiErrorDetail || '');
      setDetailsList(Array.isArray(apiDetails) ? apiDetails : []);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'rgb(247, 244, 244)', py: 8 }}>
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" mb={2}>
              Sign Up
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box component="form" onSubmit={handleSubmit}>
                {err && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {err}
                    {errorDetail && (
                      <div style={{ fontSize: 13, color: '#b71c1c', marginTop: 4 }}>{errorDetail}</div>
                    )}
                    {errorList.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {errorList.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    )}
                    {detailsList.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {detailsList.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    )}
                  </Alert>
                )}
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  error={!!errors['email']}
                  helperText={errors['email']}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  error={!!errors['password']}
                  helperText={errors['password']}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  error={!!errors['confirmPassword']}
                  helperText={errors['confirmPassword']}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors['firstName']}
                  helperText={errors['firstName']}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors['lastName']}
                  helperText={errors['lastName']}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  error={!!errors['phoneNumber']}
                  helperText={errors['phoneNumber']}
                />
                <DatePicker
                  label="Date of Birth"
                  value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : null}
                  onChange={(date) => {
                    setFormData((prev) => ({
                      ...prev,
                      dateOfBirth: date ? date.toISOString().slice(0, 10) : '',
                    }));
                    setErrors((prev) => ({ ...prev, dateOfBirth: '' }));
                  }}
                  slotProps={{ textField: {
                    margin: 'normal',
                    required: true,
                    fullWidth: true,
                    error: !!errors['dateOfBirth'],
                    helperText: errors['dateOfBirth'],
                    name: 'dateOfBirth',
                  }}}
                />
                <TextField
                  margin="normal"
                  required
                  select
                  fullWidth
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  error={!!errors['gender']}
                  helperText={errors['gender']}
                >
                  {genders.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={<Checkbox checked={formData.consentGiven} onChange={handleChange} name="consentGiven" />}
                  label="I consent to the processing of personal data"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.dataProcessingConsent} onChange={handleChange} name="dataProcessingConsent" />}
                  label="I agree to the processing of data for marketing purposes"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.marketingConsent} onChange={handleChange} name="marketingConsent" />}
                  label="I agree to receive marketing emails"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing up...' : 'Sign Up'}
                </Button>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button href="/login" variant="text">Already have an account? Sign in</Button>
                </Box>
              </Box>
            </LocalizationProvider>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Register; 